/**
 * Disponibilidade de locais pedidos a partir dos horários habituais
 * devolvidos pelo Google Places (`regularOpeningHours.periods`).
 *
 * Lógica pura e testável: não faz chamadas de rede e nunca inventa horários.
 * Feriados, eventos ou reservas podem alterar o funcionamento real do local,
 * por isso os resultados são sempre apresentados como horários habituais.
 */

export type OpeningPeriodPoint = { day?: number; hour?: number; minute?: number };
export type OpeningPeriod = { open?: OpeningPeriodPoint; close?: OpeningPeriodPoint };

/** Intervalo aberto num dia da semana (0 = domingo, como no Google Places). */
export type DaySlot = { day: number; startMinutes: number; endMinutes: number };

export type AvailabilityStatus =
  | "fits"
  | "closed_in_window"
  | "insufficient_time"
  | "hours_unavailable";

export type AlternativeSlot = {
  dayOffset: number;
  day: number;
  startMinutes: number;
  endMinutes: number;
};

const DAY_MINUTES = 24 * 60;

const clampMinutes = (value: number) => Math.max(0, Math.min(DAY_MINUTES, Math.round(value)));

/** Converte períodos do Google Places em intervalos por dia da semana. */
export function periodsToDaySlots(periods: OpeningPeriod[] | null | undefined): DaySlot[] {
  if (!periods?.length) return [];
  const slots: DaySlot[] = [];
  for (const period of periods) {
    const open = period.open;
    if (!open || typeof open.day !== "number" || typeof open.hour !== "number") continue;
    const day = ((open.day % 7) + 7) % 7;
    const start = clampMinutes(open.hour * 60 + (open.minute ?? 0));
    const close = period.close;
    if (!close || typeof close.hour !== "number") {
      // Aberto 24h (Places omite `close`).
      slots.push({ day, startMinutes: start, endMinutes: DAY_MINUTES });
      continue;
    }
    const closeDay = typeof close.day === "number" ? ((close.day % 7) + 7) % 7 : day;
    const end = clampMinutes(close.hour * 60 + (close.minute ?? 0));
    if (closeDay === day && end > start) {
      slots.push({ day, startMinutes: start, endMinutes: end });
      continue;
    }
    // Atravessa a meia-noite: parte em dois intervalos comparáveis.
    slots.push({ day, startMinutes: start, endMinutes: DAY_MINUTES });
    if (end > 0) slots.push({ day: closeDay, startMinutes: 0, endMinutes: end });
  }
  return slots.sort((a, b) => a.day - b.day || a.startMinutes - b.startMinutes);
}

/** Maior sobreposição (minutos) entre a janela pedida e os horários do dia. */
export function overlapMinutesForDay(
  slots: DaySlot[],
  day: number,
  startMinutes: number,
  endMinutes: number,
): number {
  let best = 0;
  for (const slot of slots) {
    if (slot.day !== day) continue;
    const overlap = Math.min(slot.endMinutes, endMinutes) - Math.max(slot.startMinutes, startMinutes);
    if (overlap > best) best = overlap;
  }
  return Math.max(0, best);
}

export function evaluatePlaceWindow(input: {
  slots: DaySlot[];
  day: number;
  startMinutes: number;
  endMinutes: number;
  minimumMinutes?: number;
}): { status: AvailabilityStatus; overlapMinutes: number } {
  const minimum = Math.max(1, input.minimumMinutes ?? 45);
  if (!input.slots.length) return { status: "hours_unavailable", overlapMinutes: 0 };
  const overlap = overlapMinutesForDay(input.slots, input.day, input.startMinutes, input.endMinutes);
  if (overlap <= 0) return { status: "closed_in_window", overlapMinutes: 0 };
  if (overlap < minimum) return { status: "insufficient_time", overlapMinutes: overlap };
  return { status: "fits", overlapMinutes: overlap };
}

/**
 * Sugestões derivadas exclusivamente dos horários habituais do Google Places.
 * Nunca inventa horários: só devolve intervalos realmente abertos.
 */
export function suggestAlternativeSlots(input: {
  slots: DaySlot[];
  day: number;
  startMinutes: number;
  endMinutes: number;
  minimumMinutes?: number;
  maxDaysAhead?: number;
  maxResults?: number;
}): AlternativeSlot[] {
  if (!input.slots.length) return [];
  const minimum = Math.max(1, input.minimumMinutes ?? 45);
  const requested = Math.max(minimum, input.endMinutes - input.startMinutes);
  const maxDays = input.maxDaysAhead ?? 7;
  const maxResults = input.maxResults ?? 4;
  const out: AlternativeSlot[] = [];
  const seen = new Set<string>();

  for (let offset = 0; offset < maxDays && out.length < maxResults; offset++) {
    const day = (input.day + offset) % 7;
    const daySlots = input.slots
      .filter((slot) => slot.day === day)
      .sort((a, b) => a.startMinutes - b.startMinutes);
    for (const slot of daySlots) {
      if (out.length >= maxResults) break;
      const available = slot.endMinutes - slot.startMinutes;
      if (available < minimum) continue;
      // No próprio dia, ignora intervalos que já terminaram antes do pedido.
      if (offset === 0 && slot.endMinutes <= input.startMinutes) continue;
      const start =
        offset === 0 ? Math.max(slot.startMinutes, input.startMinutes) : slot.startMinutes;
      if (slot.endMinutes - start < minimum) continue;
      const end = Math.min(slot.endMinutes, start + requested);
      // No próprio dia, não propor exatamente a janela já pedida.
      if (offset === 0 && start === input.startMinutes && end === input.endMinutes) continue;
      const key = `${offset}:${start}:${end}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ dayOffset: offset, day, startMinutes: start, endMinutes: end });
    }
  }
  return out;
}

/** "13:00" a partir de minutos do dia. */
export function formatSlotTime(minutes: number): string {
  const safe = clampMinutes(minutes);
  const h = Math.floor(safe / 60) % 24;
  const m = safe % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const WEEKDAY_LABELS: Record<string, string[]> = {
  pt: ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  es: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
  fr: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  it: ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"],
  de: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
};

const RELATIVE_LABELS: Record<string, string[]> = {
  pt: ["hoje", "amanhã", "depois de amanhã"],
  en: ["today", "tomorrow", "the day after tomorrow"],
  es: ["hoy", "mañana", "pasado mañana"],
  fr: ["aujourd'hui", "demain", "après-demain"],
  it: ["oggi", "domani", "dopodomani"],
  de: ["heute", "morgen", "übermorgen"],
};

export function formatAlternativeSlot(slot: AlternativeSlot, language: string): string {
  const lang = (language || "pt").slice(0, 2).toLowerCase();
  const relative = RELATIVE_LABELS[lang] ?? RELATIVE_LABELS.pt!;
  const weekdays = WEEKDAY_LABELS[lang] ?? WEEKDAY_LABELS.pt!;
  const dayLabel = slot.dayOffset < relative.length ? relative[slot.dayOffset]! : weekdays[slot.day]!;
  return `${dayLabel} ${formatSlotTime(slot.startMinutes)}–${formatSlotTime(slot.endMinutes)}`;
}

/**
 * Prova de janela de visita: o candidato encaixa numa janela diurna útil
 * (08:00–20:00) com duração >= visitMinutes? Horas desconhecidas NUNCA
 * desqualificam — a ausência de horário não é prova de fecho.
 */
export function candidateFitsVisitingWindow(
  periods: OpeningPeriod[] | null | undefined,
  visitMinutes: number,
): boolean {
  if (!periods?.length) return true;
  const slots = periodsToDaySlots(periods);
  const need = Math.max(15, visitMinutes);
  const DAY_START = 8 * 60;
  const DAY_END = 20 * 60;
  return slots.some(
    (slot) => Math.min(slot.endMinutes, DAY_END) - Math.max(slot.startMinutes, DAY_START) >= need,
  );
}
